import { createRoute, Link } from '@tanstack/react-router';
import { Globe, Heart } from 'lucide-react';
import { useEffect } from 'react';
import { usePreferences, applyTheme, setThemeOverride } from '../hooks/usePreferences';
import { useTranslation } from '../i18n';
import type { Locale } from '../i18n';
import { KOFI_URL } from '../constants/external';
import { Route as rootRoute } from './__root';
import styles from './terms.module.css';
import landingStyles from './index.module.css';

export const Route = createRoute({
    getParentRoute: () => rootRoute,
    path: '/terms',
    component: TermsPage,
});

function TermsPage() {
    const { preferences, setPreference } = usePreferences();
    const { t, locale } = useTranslation();
    // Force Vivid theme on public pages, restore user's theme on leave
    useEffect(() => {
        setThemeOverride(true);
        applyTheme('vivid');
        return () => {
            setThemeOverride(false);
            applyTheme(preferences.theme);
        };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const toggleLocale = (next: Locale) => setPreference('locale', next);

    const sections = [
        { title: t('terms.acceptanceTitle'), body: t('terms.acceptanceBody') },
        { title: t('terms.accountTitle'), body: t('terms.accountBody') },
        { title: t('terms.useTitle'), body: t('terms.useBody') },
        { title: t('terms.contentTitle'), body: t('terms.contentBody') },
        { title: t('terms.paidServicesTitle'), body: t('terms.paidServicesBody') },
        { title: t('terms.ipTitle'), body: t('terms.ipBody') },
        { title: t('terms.liabilityTitle'), body: t('terms.liabilityBody') },
        { title: t('terms.terminationTitle'), body: t('terms.terminationBody') },
        { title: t('terms.modificationsTitle'), body: t('terms.modificationsBody') },
        { title: t('terms.governingTitle'), body: t('terms.governingBody') },
    ];

    return (
        <div className={styles.page}>
            {/* Controls */}
            <div className={landingStyles.controls}>
                <div className={landingStyles.langPill} role="group" aria-label="Language">
                    <Globe size={13} strokeWidth={1.75} className={landingStyles.langIcon} />
                    {(['en', 'he'] as Locale[]).map((loc) => (
                        <button
                            key={loc}
                            className={`${landingStyles.langOption} ${locale === loc ? landingStyles.langOptionActive : ''}`}
                            onClick={() => toggleLocale(loc)}
                            aria-pressed={locale === loc}
                        >
                            {loc.toUpperCase()}
                        </button>
                    ))}
                </div>
            </div>

            {/* Header */}
            <div className={styles.header}>
                <Link to="/" className={styles.backLink}>
                    <span className={styles.backPrompt}>&gt; </span>
                    SiliconVault
                </Link>
                <h1 className={styles.title}>{t('terms.title')}</h1>
                <p className={styles.lastUpdated}>{t('terms.lastUpdated')}</p>
            </div>

            {/* Content */}
            <div className={styles.content}>
                <div className={styles.card}>
                    {sections.map((s) => (
                        <div key={s.title} className={styles.section}>
                            <h2 className={styles.sectionTitle}>{s.title}</h2>
                            <p className={styles.sectionBody}>{s.body}</p>
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer */}
            <footer className={styles.footer}>
                <span className={styles.footerText}>
                    &copy; {new Date().getFullYear()} Asaf Saar. {t('landing.footer')}
                </span>
                <span className={styles.footerSep}> | </span>
                <Link to="/privacy" className={styles.footerLink}>{t('footer.privacy')}</Link>
                <span className={styles.footerSep}> | </span>
                <Link to="/refund" className={styles.footerLink}>{t('footer.refund')}</Link>
                <span className={styles.footerSep}> | </span>
                <a
                    href={KOFI_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.footerSupport}
                >
                    <Heart size={10} strokeWidth={2} />
                    {t('support.footerLink')}
                </a>
            </footer>
        </div>
    );
}
